import React, { useEffect, useCallback } from 'react';
import { useFetch } from 'hooks/use-fetch';
import { useDispatch } from '@ecommerce/nest-ssr/common';
import { ProductsActions } from 'modules/shared/reducers/category-products.reducer';
import { ProductBoxWideProps } from 'modules/shared/components/product/product-box-wide/product-box-wide.props';
import { ProductBoxNarrowProps } from 'modules/shared/components/product/product-box-narrow/product-box-narrow.props';
import OfferInterface from 'interfaces/offer.interface';
import DealerInterface from 'interfaces/dealer.interface';
import { BASE_URL } from 'helpers/global.const.helper';
import {
    AD_POSITIONER_TAG,
    AD_SPONSORED_TAG,
    PRODUCTS_PER_ROW,
    RMN_POSITION_OFFSET
} from 'helpers/ads-config';

type PromiseHandler = (value: unknown) => void;

interface OffersInterface {
    category_id: number;
    id: string;
    offer_description: string;
    offer_id: string;
    offer_image: string;
    offer_name: string;
    offer_price: number;
    offer_url: string;
    offer_url_without_redirect: string;
    product_id: number;
    website_id: number;
}

interface Product extends ProductBoxWideProps, ProductBoxNarrowProps {
    offers: {
        count: number;
        items: {
            url: OfferInterface['url'];
            price: OfferInterface['price'];
            dealer: {
                id: DealerInterface['id'];
                name: DealerInterface['name'];
            };
        }[];
    };
}

interface SponsoredAdInterface {
    fields: {
        urlRedirect: string;
        feed: {
            offers: OffersInterface[];
            redirectToOffers: boolean;
        };
    };

    meta: {
        dsaurl: string;
        adclick: string;
    };

    render: () => void;
}

interface PositionAdInterface {
    fields: {
        products: {
            fields: {
                id: string;
                url: string;
                dealer_id: string | null;
            };
        }[];
    };

    meta: {
        dsaurl: string;
        adclick: string;
    };

    render: () => void;
}

interface PositionAdData {
    ad: PositionAdInterface;
    position: number;
}

interface SponsoredAdData {
    ad: SponsoredAdInterface;
    productsIds: number[];
}

const RmnPromotedProduct: React.FC = () => {
    const dispatch = useDispatch();
    const sponsored = useFetch('/api/product/products', { method: 'GET' });

    const getSponsoredProducts = useCallback((productIds: number[]): Promise<Product[]> => {
        if (!productIds.length) {
            return Promise.resolve([]);
        }

        return sponsored.get({ productIds });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const nativeAdOnRejected = (err: Error): void => {
        console.error('Ad for rmn-sponsored-product could not be loaded: ', err);
    };

    const deferredPromise = (): {
        promise: Promise<unknown>;
        resolve: PromiseHandler | null;
        reject: PromiseHandler | null;
    } => {
        let resolve: PromiseHandler | null = null;
        let reject: PromiseHandler | null = null;

        const promise = new Promise((resHandler, rejHandler) => {
            resolve = resHandler;
            reject = rejHandler;
        });

        return { promise, resolve, reject };
    };

    const createAdSlot = (
        dlApi: DlApi,
        position: number,
        adResolve: PromiseHandler | null,
        adReject: PromiseHandler | null
    ): void => {
        const slot = dlApi.defineSlot('rmn-sponsored-product', `rmn-sponsored-product-pos-${position}`);

        slot.setTargeting('pos', position);

        slot.registerTemplate({
            tplCode: '1746213/MultiPositioner',
            asyncRender: true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            renderAd: (ad: any) => {
                slot.destroy();

                if (adResolve) {
                    adResolve({
                        ad,
                        type: AD_POSITIONER_TAG,
                        position
                    });
                }
            }
        });

        slot.registerTemplate({
            tplCode: '1746213/Sponsored-Product',
            asyncRender: true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            renderAd: (ad: any) => {
                slot.destroy();

                if (adResolve) {
                    adResolve({
                        ad,
                        type: AD_SPONSORED_TAG,
                        position
                    });
                }
            }
        });

        slot.on('empty', () => {
            if (adReject) {
                adReject(0);
            }
        });

        slot.on('error', () => {
            if (adReject) {
                adReject(0);
            }
        });
    };

    const processPositionedAds = (
        positionAds: PositionAdData[],
        skpProducts: Product[]
    ): Product[] => {
        const promotedProducts: Product[] = [];

        let adIndex = 0;

        for (const positioned of positionAds) {
            let hasProducts = false;

            for (const adProduct of positioned.ad.fields.products) {
                const skpProduct = skpProducts.find(product => product.id === Number(adProduct.fields.id));

                if (skpProduct && !promotedProducts.find(item => item.id === skpProduct.id)) {
                    hasProducts = true;

                    const product = { ...skpProduct };

                    const adClick: string = positioned.ad.meta.adclick || '';
                    const dealerId = adProduct.fields.dealer_id ? Number(adProduct.fields.dealer_id) : 0;
                    const newUrl = adClick + adProduct.fields.url;

                    const offers = {
                        count: 1,
                        items: [
                            {
                                url: newUrl,
                                price: product.minPrice,
                                dealer: {
                                    id: dealerId,
                                    name: ''
                                }
                            }
                        ]
                    };

                    if (positioned.ad.meta.dsaurl) {
                        product.dsaurl = positioned.ad.meta.dsaurl;
                    }

                    product.sponsored = true;
                    product.sponsoredPosition = adIndex + RMN_POSITION_OFFSET;

                    product.url = newUrl;

                    if (product.offers.count === 0) {
                        product.offers = offers;
                    } else {
                        product.offers.count = 1;
                        product.offers.items[0].url = newUrl;
                        product.offers.items[0].dealer.name = '';
                        product.offers.items = [product.offers.items.find((item) => item.dealer.id === dealerId) || product.offers.items[0]];
                    }

                    promotedProducts.push(product);
                }
            }

            if (hasProducts) {
                positioned.ad.render();
            }

            adIndex++;
        }

        dispatch({ type: ProductsActions.ADD_PRODUCTS, payload: { products: promotedProducts } });

        return promotedProducts;
    };

    const processSponsoredAds = (
        sponsoredAds: SponsoredAdData[],
        skpProducts: Product[],
        positionedProducts: Product[]
    ): void => {
        const sponsoredProducts: Product[] = [];

        let adIndex = 0;

        for (const sponsored of sponsoredAds) {
            const sponsoredRow: Product[] = [];

            const redirectToOffers = sponsored.ad?.fields?.feed?.redirectToOffers;
            const offers: OffersInterface[] = sponsored.ad?.fields?.feed?.offers;
            const urlRedirect = sponsored.ad?.fields?.urlRedirect;
            const dsaurl = sponsored.ad?.meta?.dsaurl;
            const adclick = sponsored.ad?.meta?.adclick;

            for (const productId of sponsored.productsIds) {
                const skpProduct = skpProducts.find(product => product.id === productId);

                if (skpProduct
                    && !positionedProducts.find(positionedItem => positionedItem.id === skpProduct.id)
                    && !sponsoredProducts.find(sponsoredItem => sponsoredItem.id === skpProduct.id)
                    && !sponsoredRow.find(rowItem => rowItem.id === skpProduct.id)
                    && (sponsoredRow.length < PRODUCTS_PER_ROW)) {
                    const product = { ...skpProduct };

                    const offer = offers.find(offer => offer.product_id === product.id);
                    const urlOfferWithoutRedirect = `${offer?.offer_url_without_redirect || ''}${redirectToOffers ? urlRedirect : `${BASE_URL}${product.url}`}`;
                    const url = redirectToOffers && !urlRedirect ? offer?.offer_url : urlOfferWithoutRedirect;
                    const productUrl = `${adclick || ''}${url || product.url}`;

                    if (dsaurl) {
                        product.dsaurl = dsaurl;
                    }

                    product.url = productUrl;
                    product.sponsored = true;
                    product.sponsoredPosition = adIndex + RMN_POSITION_OFFSET;
                    product.offers.items.sort(item => item.dealer.id === offer?.website_id ? -1 : 1);
                    product.offers.count = 1;
                    product.offers.items[0].url = productUrl;

                    sponsoredRow.push(product);
                }
            }

            if (sponsoredRow.length) {
                sponsored.ad.render();

                sponsoredProducts.push(...sponsoredRow);
            }

            adIndex++;
        }

        dispatch({ type: ProductsActions.ADD_PRODUCTS, payload: { products: sponsoredProducts } });
    };

    const processSponsoredValue = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result: PromiseFulfilledResult<any>,
        sponsoredAds: SponsoredAdData[]
    ): number[] | null => {
        const ad: SponsoredAdInterface = result.value.ad;

        const offers: OffersInterface[] = ad?.fields?.feed?.offers;
        const adProductIds = offers?.map(offer => offer.product_id);

        if (adProductIds) {
            sponsoredAds.push({
                ad,
                productsIds: adProductIds
            });

            return adProductIds;
        }

        return null;
    };

    const processPositionedValue = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result: PromiseFulfilledResult<any>,
        positionAds: PositionAdData[]
    ): number[] | null => {
        const ad: PositionAdInterface = result.value.ad;

        if (ad?.fields?.products?.length) {
            positionAds.push({
                ad,
                position: result.value.position
            });

            return ad.fields.products.map(product => Number(product.fields.id));
        }

        return null;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const processAds = (results: PromiseSettledResult<any>[]): void => {
        const positionAds: PositionAdData[] = [];
        const sponsoredAds: SponsoredAdData[] = [];
        const productIds: number[] = [];

        for (const result of results) {
            if (result.status === 'fulfilled' && result.value) {
                if (result?.value?.type === AD_POSITIONER_TAG) {
                    const adProductIds = processPositionedValue(result, positionAds);

                    if (adProductIds) {
                        productIds.push(...adProductIds);
                    }
                } else if (result?.value?.type === AD_SPONSORED_TAG) {
                    const adProductIds = processSponsoredValue(result, sponsoredAds);

                    if (adProductIds) {
                        productIds.push(...adProductIds);
                    }
                }
            }
        }

        if (productIds?.length) {
            getSponsoredProducts(Array.from(new Set(productIds))).then(skpProducts => {
                if (skpProducts?.length) {
                    const positionedProducts = processPositionedAds(positionAds, skpProducts);

                    processSponsoredAds(sponsoredAds, skpProducts, positionedProducts);
                }
            });
        }
    };

    useEffect(() => {
        window?.dlApi?.cmd?.push((dlApi: DlApi): void => {
            const adPromises = [1, 2, 3].map(() => deferredPromise());
            adPromises.forEach(({ resolve, reject }, idx) => {
                createAdSlot(dlApi, idx + 1, resolve, reject);
            });

            dlApi.fetch();

            Promise.allSettled(adPromises.map(({ promise }) => promise))
                .then(processAds)
                .catch(nativeAdOnRejected);
        });
    }, [dispatch, getSponsoredProducts]);

    return null;
};

export default React.memo(RmnPromotedProduct);
